import { useEffect, useRef, useState } from 'react';
import { SelectionInfo } from 'dragonmouse-shared/devigner';
import { Frame } from 'dragonmouse-shared/interfaces';
import NumberInput from 'components/Widget/Input/NumberInput';
import Select from 'components/Widget/Select';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { applyStyleProperties } from 'visualEdits';
import { trackVisualEditAction } from 'analytics';
import Field from '../Field';

interface TypographyEditControlProps {
	frame: Frame;
	selectionInfo: SelectionInfo;
}

const FONT_WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

// Parse "16px" → 16. Falls back to 16 (browser default body size) if missing
// or unparseable. Using the inherited computed value, which is always concrete.
const parseFontSize = (raw: string | undefined): number => {
	if (!raw) return 16;
	const parsed = parseFloat(raw);
	return isNaN(parsed) ? 16 : parsed;
};

// getComputedStyle resolves font-weight to a numeric string ("400", "700").
// Snap to the nearest 100 in case some browser returns an off-grid value.
const parseFontWeight = (raw: string | undefined): number => {
	if (!raw) return 400;
	const parsed = parseInt(raw, 10);
	if (isNaN(parsed)) return 400;
	const snapped = Math.round(parsed / 100) * 100;
	return Math.max(100, Math.min(900, snapped));
};

const TypographyEditControl = ({ frame, selectionInfo }: TypographyEditControlProps) => {
	const elementId = selectionInfo.dataset?.mcElementId ?? selectionInfo.element.id;

	const initialSize = parseFontSize(selectionInfo.computedStyles?.fontSize);
	const initialWeight = parseFontWeight(selectionInfo.computedStyles?.fontWeight);

	const [fontSize, setFontSize] = useState(initialSize);
	const [fontWeight, setFontWeight] = useState(initialWeight);
	const debouncedSize = useDebouncedValue(fontSize);

	const initialSizeRef = useRef(initialSize);
	const initialWeightRef = useRef(initialWeight);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	useEffect(() => {
		if (debouncedSize === initialSizeRef.current) return;
		void commit('font-size', `${debouncedSize}px`);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSize]);

	// Font weight is a discrete pick — commit immediately, no debounce.
	useEffect(() => {
		if (fontWeight === initialWeightRef.current) return;
		void commit('font-weight', String(fontWeight));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fontWeight]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const commit = async (property: string, value: string) => {
		if (!frame.siteId) return;
		trackVisualEditAction('commit_typography_edit');
		await applyStyleProperties(frame.id, frame.siteId, elementId, { [property]: value });
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<>
			<Field label="Font size">
				<NumberInput label="px" value={fontSize} onChange={setFontSize} min={1} max={500} />
			</Field>
			<Field label="Font weight">
				<Select
					value={String(fontWeight)}
					onChange={(e) => setFontWeight(parseInt(e.target.value, 10))}
				>
					{FONT_WEIGHTS.map((w) => (
						<option key={w} value={w}>
							{w}
						</option>
					))}
				</Select>
			</Field>
		</>
	);
};

export default TypographyEditControl;
